import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Dimensions, ServiceDetailsModel } from '~/lib/api/smartsend/types';
import Loading from '../../../../../components/loading/Loading';
import {
  P2GBox,
  P2GGrid,
  P2GText,
  P2GLink,
  SvgMaxHeight,
  SvgMaxLength,
  SvgMaxWeight,
  SvgMaxWidth,
  SvgProtectionAdd,
  SvgShop,
  SvgVan,
  SvgProhibited,
  SvgEnquiryExclamation,
  SvgCustoms,
  SvgPrinter,
  P2GButtonGroup,
  P2GButtonGroupItem
} from '@parcel2go/core-ui';
import SidePanelSection, { SidePanelSections } from '../../../../../components/sidepanel-section';
import AlertWithIcon from '../../AlertWithIcon';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { P2GAlert } from '@parcel2go/core-ui';
import { ServiceRestrictions } from './ServiceRestrictions';
import { ProtectionIsEnabledForService } from '../../../lib/helpers/ProtectionIsEnabled';
import { GetServiceRestrictions } from './utils/GetServiceRestrictions';
import { useSmartSendApi } from '~/lib/api/smartsend/use-smartsend-api';
import { Parcel2GoProvider, SmartSendProvider } from '~/lib/consts/providers';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { Trans, useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import CdnIconProxy from '~/apps/account-linking/components/CdnIconProxy';
import { CountryCache, GenCountryKeyForDeliveryCountries } from '~/apps/smartsend/lib/cache/CountryCache';

export type ServiceTabType = '' | 'INFO' | 'CUSTOMS' | 'PRINTER' | 'DELIVERYTYPE';

interface props {
  id: string;
  provider: string;
  accountId: string;
  comparison?: Dimensions;
  senderIsoCode?: string;
  senderSubdivision?: string;
  recipientIsoCode?: string;
  recipientSubdivision?: string;
  itemId: string;
  tab: ServiceTabType;
  setTab: (tab: ServiceTabType) => void;
  currencyCode: string;
}
const ServiceInfo: React.FC<props> = (props) => {
  const { getServiceDetails } = useSmartSendApi();
  const [service, setService] = useState<ServiceDetailsModel>();
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);

  const errors = useMemo(
    () =>
      GetServiceRestrictions(
        service,
        props.comparison,
        props.senderIsoCode,
        props.senderSubdivision,
        props.recipientIsoCode,
        props.recipientSubdivision
      ),
    [service, props.comparison, props.recipientIsoCode, props.senderIsoCode]
  );

  const setTab = (t: string) => props.setTab(t as ServiceTabType);

  useEffect(() => {
    async function load() {
      const r = await getServiceDetails(props.id, props.provider, props.accountId);
      if (r.success) {
        setService(r.result.Service);
      }
    }
    if (props.id) {
      load();
    } else {
      setService(null);
    }
  }, [props.id]);

  if (!props.id || !props.provider || !props.accountId) {
    return null;
  }

  if (!service) {
    return <Loading />;
  }

  function renderRowInfo(iconSrc: string, svg: ReactNode, info: ReactNode, isError?: boolean) {
    return (
      <P2GBox mb={1}>
        <P2GGrid container alignItems="center" wrap="nowrap" gap={2}>
          {svg && (
            <P2GGrid item flexShrink={1}>
              <CdnIconProxy palette={isError ? 'error' : 'primary'} size="sm" src={iconSrc}>
                {svg}
              </CdnIconProxy>
            </P2GGrid>
          )}
          <P2GGrid item flexGrow={1}>
            <P2GText colour={isError ? 'error' : 'inherit'} variant="body2">
              {info}
            </P2GText>
          </P2GGrid>
        </P2GGrid>
      </P2GBox>
    );
  }

  return (
    <>
      <P2GBox mb={2}>
        <P2GButtonGroup fullWidth>
          <P2GButtonGroupItem value={'INFO'} selected={props.tab} onClick={setTab}>
            {t('INFO_TAB', { defaultValue: 'Info' })}
          </P2GButtonGroupItem>
          <P2GButtonGroupItem value={'PRINTER'} selected={props.tab} onClick={setTab}>
            {t('LABELS_TAB', { defaultValue: 'Labels' })}
          </P2GButtonGroupItem>
          <P2GButtonGroupItem value={'DELIVERYTYPE'} selected={props.tab} onClick={setTab}>
            {t('DELIVER_TAB', { defaultValue: 'Delivery' })}
          </P2GButtonGroupItem>
          {service?.Tags?.includes('DDP') && (
            <P2GButtonGroupItem value={'CUSTOMS'} selected={props.tab} onClick={setTab}>
              {t('CUSTOMS_TAB', { defaultValue: 'Customs' })}
            </P2GButtonGroupItem>
          )}
        </P2GButtonGroup>
      </P2GBox>

      <SidePanelSections>
        <ServiceRestrictions errors={errors} />

        {props.tab == 'INFO' && (
          <>
            <SidePanelSection title={t('RESTRICTIONS_SIDEPANEL_TITLE', { defaultValue: 'Restrictions' })}>
              <P2GBox pt={2}>
                {[Parcel2GoProvider, SmartSendProvider].includes(service.Provider) &&
                  renderRowInfo(
                    '/theme/icons/Prohibited.svg',
                    <SvgProhibited />,
                    <>
                      <P2GLink href={SettingsCache.settings.Links.ProhibitedItems} target="_blank">
                        {t('CHECK_PROHIBITED_ITEMS_LINK', { defaultValue: 'Check the prohibited item list' })}
                      </P2GLink>
                    </>
                  )}
                {service.MaxWeight &&
                  renderRowInfo(
                    '/theme/icons/Weight.svg',
                    <SvgMaxWeight />,
                    <>
                      <Trans
                        t={t}
                        i18nKey="MAX_WEIGHT_INFO"
                        values={{ weight: service.MaxWeight }}
                        defaults="Maximum Weight {{weight}} Kg."
                      />
                    </>
                  )}
                {service.MaxLength &&
                  renderRowInfo(
                    '/theme/icons/MaxLength.svg',
                    <SvgMaxLength />,
                    <>
                      <Trans
                        t={t}
                        i18nKey="MAX_LENGTH_INFO"
                        values={{ length: service.MaxLength }}
                        defaults="Maximum Length {{length}} m."
                      />
                    </>
                  )}
                {service.MaxWidth &&
                  renderRowInfo(
                    '/theme/icons/MaxWidth.svg',
                    <SvgMaxWidth />,
                    <>
                      <Trans
                        t={t}
                        i18nKey="MAX_WIDTH_INFO"
                        values={{ width: service.MaxWidth }}
                        defaults="Maximum Width {{width}} m."
                      />
                    </>
                  )}
                {service.MaxHeight &&
                  renderRowInfo(
                    '/theme/icons/MaxHeight.svg',
                    <SvgMaxHeight />,
                    <>
                      <Trans
                        t={t}
                        i18nKey="MAX_HEIGHT_INFO"
                        values={{ height: service.MaxHeight }}
                        defaults="Maximum Height {{height}} m."
                      />
                    </>
                  )}
              </P2GBox>
            </SidePanelSection>
            <SidePanelSection title={t('KEY_INFORMATION_SIDEPANEL_TITLE', { defaultValue: 'Key Information' })}>
              <P2GBox pt={2}>
                {service.MaxCover > 0 &&
                  ProtectionIsEnabledForService(service) &&
                  renderRowInfo(
                    '/theme/icons/ProtectionAdd.svg',
                    <SvgProtectionAdd />,
                    <>
                      <Trans
                        t={t}
                        i18nKey="PROTECT_YOUR_PARCEL_INFO"
                        values={{ amount: toDisplayCurrency(props.currencyCode, service.MaxCover) }}
                        defaults="Protect your parcel up to the value of {{amount}}."
                      />
                    </>
                  )}
                {service.IsPrinterRequired &&
                  renderRowInfo(
                    '/theme/icons/ProtectionAdd.svg',
                    <SvgPrinter />,
                    <>
                      {t('PRINT_LABEL_INFO', { defaultValue: 'With this service you will need to print your label.' })}
                    </>
                  )}
                {renderRowInfo(
                  null,
                  null,
                  <>
                    {service.ShortDescriptions ? (
                      <span
                        dangerouslySetInnerHTML={{
                          __html: `${service.ShortDescriptions.replaceAll(`<a `, `<a target='_blank' `)}`
                        }}
                      />
                    ) : (
                      service.Provider === SmartSendProvider && (
                        <>
                          {t('CHECK_WEBSITE_FOR_DETAILS', {
                            defaultValue: 'Please check website for details of this service.'
                          })}
                        </>
                      )
                    )}
                  </>
                )}
              </P2GBox>
            </SidePanelSection>
          </>
        )}
        {props.tab == 'PRINTER' && (
          <SidePanelSection title={t('PRINTING_SIDEPANEL_TITLE', { defaultValue: 'Printing' })}>
            <P2GBox pt={2}>
              {service.IsPrinterRequired
                ? renderRowInfo(
                    '/theme/icons/ProtectionAdd.svg',
                    <SvgPrinter />,
                    <>
                      {t('PRINT_LABEL_INFO', { defaultValue: 'With this service you will need to print your label.' })}
                    </>
                  )
                : renderRowInfo(
                    '/theme/icons/ProtectionAdd.svg',
                    <SvgPrinter />,
                    <>
                      {t('LABEL_NOT_REQUIRED_INFO', {
                        defaultValue: 'You are not required to print the label for this service.'
                      })}
                    </>
                  )}
            </P2GBox>
          </SidePanelSection>
        )}

        {props.tab == 'CUSTOMS' && (
          <>
            <SidePanelSection title={t('CUSTOMS_SIDEPANEL_TITLE', { defaultValue: 'Customs' })}>
              {service?.Tags?.includes('DDP') == true ? (
                <>
                  <P2GBox mb={2}>
                    {renderRowInfo(
                      '/theme/icons/Customs.svg',
                      <SvgCustoms />,
                      <>
                        {t('DELIVERY_DUTY_PAID_INFO', {
                          defaultValue: 'Delivery Duty Paid is available for this service.'
                        })}
                      </>
                    )}
                  </P2GBox>
                  <P2GAlert color="info">
                    <P2GText variant="body2" component="p">
                      {t('DDP_INTRO_INFO', {
                        defaultValue: `DDP is where you pay all customs, duties and fees before sending goods to your recipient in an EU 
                        country. This is a much better option for your recipient, as it avoids them encountering any nasty surprise charges.`
                      })}
                    </P2GText>
                    <P2GText variant="body2" component="p">
                      <Trans
                        t={t}
                        i18nKey="DDP_AS_PAYMENT_OPTION_INFO"
                        components={{ sup: <sup /> }}
                        defaults="As of July 1<sup>st</sup> 2021, DDP can be selected as a payment option during the order process on selected services."
                      />
                    </P2GText>
                    <P2GText variant="body2" component="p">
                      <Trans
                        t={t}
                        i18nKey="DDP_CHEAPER_THAN_DDU_INFO"
                        defaults="<strong>Remember:</strong> DDP is also cheaper and quicker than sending by DDU"
                      />
                    </P2GText>
                  </P2GAlert>
                </>
              ) : (
                <P2GBox pt={2}>
                  {renderRowInfo(
                    '/theme/icons/Customs.svg',
                    <SvgCustoms />,
                    <>
                      {t('DELIVERY_DUTY_PAID_INFO', {
                        defaultValue: 'Delivery Duty Paid is available for this service.'
                      })}
                    </>
                  )}
                </P2GBox>
              )}
            </SidePanelSection>
          </>
        )}

        {props.tab == 'DELIVERYTYPE' && (
          <>
            <SidePanelSection
              title={t('COLLECTION_DELIVERY_SIDEPANEL_TITLE', { defaultValue: 'Collection/Delivery Details' })}
            >
              {renderRowInfo(
                service.CollectionType === 'Collection' ? '/theme/icons/Van.svg' : '/theme/icons/Shop.svg',
                service.CollectionType === 'Collection' ? <SvgVan /> : <SvgShop />,
                <span>
                  {service.CollectionType === 'Collection'
                    ? 'This is a collection service.'
                    : 'This service requires you to Drop Off your parcel.'}
                </span>
              )}
            </SidePanelSection>

            <SidePanelSection title={t('DELIVER_TO_SECTION_TITLE', { defaultValue: 'Deliveries to:' })}>
              <P2GGrid container spacing={1}>
                {service.DeliveryCountries.map((c) => (
                  <P2GGrid item xs={6} key={`${c.Iso3Code}_${c.Subdivision}`}>
                    <P2GText>{CountryCache[GenCountryKeyForDeliveryCountries(c)]?.LocalisedName}</P2GText>
                  </P2GGrid>
                ))}
              </P2GGrid>
              {props.recipientIsoCode &&
                !service.DeliveryCountries.find((x) => x.Iso3Code === props.recipientIsoCode) && (
                  <AlertWithIcon icon={<SvgEnquiryExclamation />} color="error">
                    <P2GText variant="body2">
                      <Trans
                        t={t}
                        i18nKey="SERVICE_INFO_RECIPIENT_COUNTRY_NOT_COVERED"
                        values={{ country: props.recipientIsoCode }}
                        defaults="Your recipient's country (<strong>{{country}}</strong>) is not covered by this service."
                      />
                    </P2GText>
                  </AlertWithIcon>
                )}
            </SidePanelSection>

            <SidePanelSection title={t('COLLECTION_FROM_SECTION_TITLE', { defaultValue: 'Collections from:' })}>
              <P2GGrid container spacing={1}>
                {service.CollectionCountries.map((c) => (
                  <P2GGrid item xs={6} key={`${c.Iso3Code}_${c.Subdivision}`}>
                    <P2GText>{CountryCache[GenCountryKeyForDeliveryCountries(c)]?.LocalisedName}</P2GText>
                  </P2GGrid>
                ))}
              </P2GGrid>
              {props.senderIsoCode && !service.CollectionCountries.find((x) => x.Iso3Code === props.senderIsoCode) && (
                <AlertWithIcon icon={<SvgEnquiryExclamation />} color="error">
                  <P2GText>
                    <Trans
                      t={t}
                      i18nKey="SERVICE_INFO_SENDER_COUNTRY_NOT_COVERED"
                      values={{ country: props.senderIsoCode }}
                      defaults="Your sender's country (<strong>{{country}}</strong>) is not covered by this service."
                    />
                  </P2GText>
                </AlertWithIcon>
              )}
            </SidePanelSection>
          </>
        )}
      </SidePanelSections>
    </>
  );
};
export default ServiceInfo;
